import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import companyAccountsService from '../../services/companyAccountsService';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import AccountsTable from './components/AccountsTable';
import AccountFilters from './components/AccountFilters';
import QuickActionsModal from './components/QuickActionsModal';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const CompanyAccountsManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    complianceRange: 'all',
    billingStatus: 'all',
    tier: 'all',
    searchTerm: ''
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [showTeaserEditModal, setShowTeaserEditModal] = useState(false);

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [accounts, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [accountsData, summaryData] = await Promise.all([
        companyAccountsService?.getAllCompanyAccounts(),
        companyAccountsService?.getExecutiveSummary()
      ]);

      setAccounts(accountsData);
      setSummary(summaryData);
    } catch (err) {
      setError(err?.message || 'Failed to load company accounts data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...accounts];

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(account =>
        account?.companyName?.toLowerCase()?.includes(term) ||
        account?.companyLegalName?.toLowerCase()?.includes(term) ||
        account?.contactEmail?.toLowerCase()?.includes(term)
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(account => {
        if (filters?.status === 'active') return account?.isActive && account?.subscriptionStatus === 'active';
        if (filters?.status === 'suspended') return account?.subscriptionStatus === 'suspended';
        if (filters?.status === 'inactive') return !account?.isActive;
        return true;
      });
    }

    // Compliance range filter
    if (filters?.complianceRange !== 'all') {
      filtered = filtered?.filter(account => {
        const score = account?.complianceScore || 0;
        if (filters?.complianceRange === 'excellent') return score >= 90;
        if (filters?.complianceRange === 'good') return score >= 70 && score < 90;
        if (filters?.complianceRange === 'warning') return score >= 50 && score < 70;
        if (filters?.complianceRange === 'critical') return score < 50;
        return true;
      });
    }

    // Billing status filter
    if (filters?.billingStatus !== 'all') {
      filtered = filtered?.filter(account => {
        if (!account?.nextBillingDate) return filters?.billingStatus === 'overdue';
        const daysUntilBilling = Math.floor(
          (new Date(account?.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        if (filters?.billingStatus === 'current') return daysUntilBilling > 7;
        if (filters?.billingStatus === 'due_soon') return daysUntilBilling >= 0 && daysUntilBilling <= 7;
        if (filters?.billingStatus === 'overdue') return daysUntilBilling < 0;
        return true;
      });
    }

    // Tier filter
    if (filters?.tier !== 'all') {
      filtered = filtered?.filter(account => account?.subscriptionTier === filters?.tier);
    }

    setFilteredAccounts(filtered);
  };

  const handleQuickAction = (account) => {
    setSelectedAccount(account);
    setShowActionsModal(true);
  };

  const handleActionComplete = async () => {
    setShowActionsModal(false);
    setSelectedAccount(null);
    await loadData();
  };

  const handleExport = () => {
    try {
      // Prepare CSV data
      const headers = [
        'Company Name',
        'Legal Name',
        'Contact Email',
        'Employees',
        'Compliance Score',
        'Status',
        'Subscription Tier',
        'Billing Status',
        'Next Billing Date'
      ];

      const rows = filteredAccounts?.map(account => [
        account?.companyName || '',
        account?.companyLegalName || '',
        account?.contactEmail || '',
        account?.employeeCount || 0,
        account?.complianceScore || 0,
        account?.isActive ? 'Active' : 'Inactive',
        account?.subscriptionTier || '',
        account?.subscriptionStatus || '',
        account?.nextBillingDate || ''
      ]);

      // Create CSV content
      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `company-accounts-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      setError('Failed to export accounts data');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading company accounts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />

      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Company Accounts Management</h1>
              <p className="text-muted-foreground">Centralized oversight for all client organizations</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
            >
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Executive Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <ExecutiveSummaryCard
              title="Total Accounts"
              value={summary?.totalAccounts}
              subtitle={`${summary?.activeAccounts} active`}
              icon="Building2"
              trend={null}
              status="default"
            />
            <ExecutiveSummaryCard
              title="Avg Compliance Score"
              value={`${summary?.avgComplianceScore}%`}
              subtitle="System-wide average"
              icon="Shield"
              trend={summary?.avgComplianceScore >= 80 ? 'up' : 'down'}
              status={summary?.avgComplianceScore >= 80 ? 'compliant' : 'warning'}
            />
            <ExecutiveSummaryCard
              title="Monthly Revenue"
              value={`K${summary?.totalRevenueKina?.toLocaleString()}`}
              subtitle="Active subscriptions"
              icon="DollarSign"
              trend="up"
              status="compliant"
            />
            <ExecutiveSummaryCard
              title="Billing Health"
              value={`${summary?.billingHealthPercentage}%`}
              subtitle={`${summary?.criticalIssues} critical issues`}
              icon="CreditCard"
              trend={summary?.billingHealthPercentage >= 90 ? 'up' : 'down'}
              status={summary?.billingHealthPercentage >= 90 ? 'compliant' : 'warning'}
            />
          </div>
        )}

        {/* Filters */}
        <AccountFilters
          filters={filters}
          onFilterChange={setFilters}
          totalAccounts={accounts?.length}
          filteredCount={filteredAccounts?.length}
        />

        {/* Accounts Table */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-foreground">Company Accounts</h2>
            <p className="text-sm text-muted-foreground">
              Showing {filteredAccounts?.length} of {accounts?.length} accounts
            </p>
          </div>

          <AccountsTable
            accounts={filteredAccounts}
            onQuickAction={handleQuickAction}
          />
        </div>
      </main>

      {showActionsModal && selectedAccount && (
        <QuickActionsModal
          account={selectedAccount}
          onClose={() => setShowActionsModal(false)}
          onActionComplete={handleActionComplete}
        />
      )}

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default CompanyAccountsManagement;